import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Formik, Form, FormikValues } from 'formik';
import moment from 'moment';
import { RootState } from 'store';
import * as yup from 'yup';

import { autosSetTraveler } from 'action-creators/autos';
import { flightsSetTraveler } from 'action-creators/flights';
import { hotelsSetTraveler } from 'action-creators/hotels';
import { userAccountData } from 'action-creators/user';
import { fetchNationalityCountryList } from 'actions/i18n';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Button from 'components/Buttons/Button';
import FormikSubmitErrorEffect from 'components/FormikSubmitErrorEffect';
import Input from 'components/Input';
import MonthSelect from 'components/MonthSelect';
import Section from 'components/Section';
import GenreSelector from 'components/Selects/GenreSelector';
import NationalitySelector from 'components/Selects/OnboardingSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { ProductType } from 'models/enums/productType';
import { UserData } from 'screens/components/Booking/ConfirmProfile';
import TravelerLoyaltyContainer from 'screens/containers/TravelerLoyalty';
import { updateProfile, UpdateProfileRequest } from 'services/ bookingService';
import { Loyalty } from 'services/loyaltiesService';
import { formatDateToBack, formatISODateToDDMMYYYY } from 'utils/generic';
import { firstCapitalize } from 'utils/string';

import {
  Field,
  Line,
  ButtonContainer,
  Content,
  ErrorValidation,
  SectionDateOfBirth,
  LoyaltyLogo,
  LoyaltyWrapper,
  LoyaltyName,
  LoyaltyNumber,
} from './styles';

type Props = {
  user: UserData;
  isInternationalFlight?: boolean;
  loyaltyProgram?: Loyalty;
  productType: ProductType;
  onSave: () => void;
  onSaveLoyaltyProgram?: () => void;
};

const ProfileEdit = ({
  user,
  isInternationalFlight,
  onSave,
  loyaltyProgram,
  onSaveLoyaltyProgram,
  productType,
}: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const toast = useToast();
  const currentUser = useUserAccount();
  const hotelTraveler = useSelector((state: RootState) => state.hotels.traveler);
  const flightTraveler = useSelector((state: RootState) => state.flights.traveler);
  const autoTraveler = useSelector((state: RootState) => state.autos.traveler);
  const [month, setMonth] = useState<number>();

  const {
    i18n: { loadingNationalityCountries, nationalityCountries },
  } = useSelector((state: RootState) => state);
  const { t } = useStoreTranslation();

  useEffect(() => {
    if (!nationalityCountries.length && !loadingNationalityCountries) {
      dispatch(fetchNationalityCountryList());
    }
  }, [dispatch, nationalityCountries, loadingNationalityCountries]);

  const validationSchema = yup.object().shape({
    name: yup.string().trim().max(100).required(),
    lastName: yup.string().trim().max(100).required(),
    phoneNumber: yup.string().phone().required(),
    email: yup.string().email().required(),
    nationality: yup.string().required(),
    birthDate_day: yup.number().positive().moreThan(0).lessThan(32, t('guestForm.day.invalidNumber')).required(),
    birthDate_year: yup
      .number()
      .moreThan(1910)
      .lessThan(new Date().getFullYear() + 1, t('guestForm.year.invalidNumber'))
      .required(),
    documentNumber: yup.string().trim().max(100).required(),
    passportNumber: isInternationalFlight ? yup.string().trim().max(100).required() : yup.string().trim().max(100),
  });

  const initValues = {
    userId: user?.userId || '',
    name: user?.name || '',
    lastName: user?.lastName || '',
    phoneNumber: user?.phoneNumber || '',
    email: user?.email || '',
    nationality: user?.nationality || '',
    gender: user?.gender,
    birthDate:
      typeof user?.birthDate === 'string' && user.birthDate.includes('/')
        ? user.birthDate
        : new Date(user?.birthDate || '0001-01-01T00:00:00Z').toISOString() === '0001-01-01T00:00:00Z'
        ? ''
        : formatISODateToDDMMYYYY(user?.birthDate) || '',
    birthDate_day:
      user?.birthDate === '0001-01-01T00:00:00Z' || user?.birthDate === undefined
        ? ''
        : moment.utc(user?.birthDate)?.format('DD') || '',
    birthDate_month:
      user?.birthDate === '0001-01-01T00:00:00Z' || user?.birthDate === undefined
        ? ''
        : moment.utc(user?.birthDate)?.format('MM') || '',
    birthDate_year:
      user?.birthDate === '0001-01-01T00:00:00Z' || user?.birthDate === undefined
        ? ''
        : moment.utc(user?.birthDate)?.format('YYYY') || '',
    documentNumber: user?.documentNumber || '',
    passportNumber: user?.passportNumber || '',
    loyaltyNumber: loyaltyProgram?.loyaltyNumber,
  };

  const handleSubmit = useCallback(
    async (value: UserData, monthValue: number | undefined) => {
      const formattedMonth = monthValue && monthValue < 10 ? '0' + monthValue : monthValue;
      const birthDate = `${value.birthDate_day}/${formattedMonth}/${value.birthDate_year}`;

      try {
        setSubmitting(true);
        await updateProfile({
          ...value,
          birthDate: formatDateToBack(birthDate),
        } as UpdateProfileRequest);

        if (currentUser?.id === value.userId) {
          dispatch(
            userAccountData({
              ...currentUser,
              ...(value as UpdateProfileRequest),
              birthDate: formatDateToBack(birthDate),
            }),
          );
        }

        if (
          productType === ProductType.Flight &&
          (flightTraveler?.id === value.userId || flightTraveler?.userId === value.userId)
        ) {
          dispatch(
            flightsSetTraveler({
              ...flightTraveler,
              ...(value as UpdateProfileRequest),
              country: value.nationality as string,
              birthDate: formatDateToBack(birthDate),
            }),
          );
        }

        if (
          productType === ProductType.Hotel &&
          (hotelTraveler?.id === value.userId || hotelTraveler?.userId === value.userId)
        ) {
          dispatch(
            hotelsSetTraveler({
              ...hotelTraveler,
              ...(value as UpdateProfileRequest),
              country: value.nationality as string,
              birthDate: formatDateToBack(birthDate),
            }),
          );
        }

        if (
          productType === ProductType.Vehicle &&
          (autoTraveler?.id === value.userId || autoTraveler?.userId === value.userId)
        ) {
          dispatch(
            autosSetTraveler({
              ...autoTraveler,
              ...(value as UpdateProfileRequest),
              country: value.nationality as string,
              birthDate: formatDateToBack(birthDate),
            }),
          );
        }

        onSave();
      } catch (e) {
        toast.add({ type: 'danger', message: t('defaultError.default') });
      } finally {
        setSubmitting(false);
      }
    },
    [autoTraveler, currentUser, dispatch, flightTraveler, hotelTraveler, onSave, t, toast, productType],
  );

  const isDateOfBirthValid = () => {
    const birthDate_day = (document.getElementById('input_birthDate_day') as HTMLInputElement)?.value || '';
    const birthDate_year = (document.getElementById('input_birthDate_year') as HTMLInputElement)?.value || '';

    if (birthDate_day === '' && birthDate_year === '') {
      return true;
    }

    return moment(`${birthDate_year}-${month}-${birthDate_day}`, 'YYYY-MM-DD').isValid();
  };

  const handleChangeMonth = (monthValue: number) => {
    setMonth(monthValue);
  };

  useEffect(() => {
    if (user?.birthDate !== '0001-01-01T00:00:00Z') {
      setMonth(parseInt(moment.utc(user?.birthDate).format('MM')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showLoyalty, setShowLoyalty] = useState<boolean>(false);

  const onCloseLoyaltyPrograms = () => {
    if (onSaveLoyaltyProgram) {
      onSaveLoyaltyProgram();
    }

    setShowLoyalty(false);
  };

  return (
    <Formik
      initialStatus={{ rightCheckIfIsValid: true }}
      initialValues={initValues}
      validationSchema={validationSchema}
      onSubmit={(value: UserData) => handleSubmit(value, month)}
      isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
    >
      {() => (
        <>
          <FormikSubmitErrorEffect />

          <Form>
            <Section title={t('booking.confirmProfile.profileEdit.sections.basicInformations')}>
              <Field name="name" label={t('booking.confirmProfile.profileEdit.fields.name')}>
                <Input placeholder={t('booking.confirmProfile.profileEdit.fields.namePlaceholder')} />
              </Field>
              <Field name="lastName" label={t('booking.confirmProfile.profileEdit.fields.lastName')}>
                <Input placeholder={t('booking.confirmProfile.profileEdit.fields.lastNamePlaceholder')} />
              </Field>
              <Field name="email" labelKey="booking.confirmProfile.profileEdit.fields.email">
                <Input id="email" type="email" disabled={!!(user && !!user.email)} />
              </Field>
              <Field name="phoneNumber" label={t('booking.confirmProfile.profileEdit.fields.phoneNumber')}>
                <Input
                  type="text"
                  inputMode="tel"
                  placeholder={t('booking.confirmProfile.profileEdit.fields.telNumberPlaceHolder')}
                />
              </Field>
            </Section>
            <SectionDateOfBirth>
              <div></div>
              <Content>
                <Field name="birthDate_day" label={t('guestForm.day.label')}>
                  <Input
                    type="text"
                    id="input_birthDate_day"
                    mask="99"
                    inputMode="numeric"
                    placeholder={t('guestForm.day.placeholder')}
                  />
                </Field>
                <Field name="birthDate_month">
                  <MonthSelect
                    handleChangeDate={handleChangeMonth}
                    initialValue={month?.toString()}
                    name="input_birthDate_month"
                  />
                </Field>
                <Field name="birthDate_year" label={t('guestForm.year.label')}>
                  <Input
                    type="text"
                    id="input_birthDate_year"
                    mask="9999"
                    inputMode="numeric"
                    placeholder={t('guestForm.year.placeholder')}
                  />
                </Field>
                {!isDateOfBirthValid() && (
                  <>
                    <ErrorValidation>{t('guestForm.dateOfBirthInvalid')}</ErrorValidation>
                  </>
                )}
              </Content>
            </SectionDateOfBirth>
            <Line />

            <Section title={t('booking.confirmProfile.profileEdit.sections.travelInformations')}>
              <Field name="gender" labelKey="booking.confirmProfile.profileEdit.fields.genre">
                <GenreSelector id="gender" />
              </Field>
              <Field name="documentNumber" labelKey="booking.confirmProfile.profileEdit.fields.document">
                <Input
                  id="documentNumber"
                  placeholder={t('booking.confirmProfile.profileEdit.fields.documentPlaceholder')}
                />
              </Field>
              <Field
                name="passportNumber"
                labelKey={
                  isInternationalFlight
                    ? 'booking.confirmProfile.profileEdit.fields.passport'
                    : 'booking.confirmProfile.profileEdit.fields.passportOptional'
                }
              >
                <Input
                  id="passportNumber"
                  placeholder={t('booking.confirmProfile.profileEdit.fields.passportPlaceholder')}
                />
              </Field>
              <Field name="nationality" labelKey={t('booking.confirmProfile.profileEdit.fields.nationality')}>
                <NationalitySelector
                  type="nationality"
                  options={nationalityCountries}
                  title={t('booking.confirmProfile.profileEdit.fields.nationalitySelect')}
                  enableSearch={true}
                />
              </Field>
            </Section>
            {productType === ProductType.Flight &&
              loyaltyProgram &&
              loyaltyProgram.loyaltyName &&
              (flightTraveler?.id === currentUser?.id || flightTraveler?.userId === currentUser?.id) && (
                <Fragment>
                  <Section title={t('booking.confirmProfile.profileEdit.loyaltyProgramTitle')}>
                    <Field name="loyaltyProgram" labelKey="">
                      <Fragment>
                        {!!loyaltyProgram?.loyaltyName && !!loyaltyProgram?.loyaltyNumber && (
                          <LoyaltyWrapper>
                            <LoyaltyLogo airline={loyaltyProgram.loyaltyId} />
                            <LoyaltyName>{loyaltyProgram?.loyaltyName}:</LoyaltyName>
                            <LoyaltyNumber>{loyaltyProgram?.loyaltyNumber}</LoyaltyNumber>
                          </LoyaltyWrapper>
                        )}
                        {loyaltyProgram?.loyaltyName && (
                          <Fragment>
                            <Button
                              type="button"
                              onClick={() => setShowLoyalty(true)}
                              invertedColor={true}
                              disabled={submitting}
                              loading={submitting}
                            >
                              {firstCapitalize(t('booking.confirmProfile.profileEdit.manageLoyaltyPrograms'))}
                            </Button>
                          </Fragment>
                        )}
                      </Fragment>
                    </Field>
                  </Section>
                </Fragment>
              )}

            <ButtonContainer>
              <Button type="submit" disabled={submitting || !isDateOfBirthValid()} loading={submitting}>
                {t('booking.confirmProfile.profileEdit.submitButton')}
              </Button>
            </ButtonContainer>

            <ApplicationOverlayContent show={showLoyalty} onClose={() => onCloseLoyaltyPrograms()}>
              <TravelerLoyaltyContainer onClose={() => onCloseLoyaltyPrograms()} />
            </ApplicationOverlayContent>
          </Form>
        </>
      )}
    </Formik>
  );
};

export default ProfileEdit;
